import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const UpdateAccountTimezoneMutation = graphql(/* GraphQL */ `
  mutation updateAccountTimezone($input: UpdateAccountTimezoneInput!) {
    updateAccountTimezone(input: $input) {
      __typename
      ... on UpdateAccountTimezoneSuccess {
        timezone
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

type UseUpdateTimeZonePreferencesResponse = {
  updateTimeZone: ({ timeZone }: { timeZone: string }) => Promise<void>
  isLoading: boolean
  hasError: boolean
}
export const useUpdateTimeZonePreferences =
  (): UseUpdateTimeZonePreferencesResponse => {
    const [executeUpdateAccountTimezone, { loading, error }] = useMutation(
      UpdateAccountTimezoneMutation,
    )

    const updateTimeZone = useCallback(
      async ({ timeZone }: { timeZone: string }) => {
        try {
          const { data, error: mutationError } =
            await executeUpdateAccountTimezone({
              variables: {
                input: {
                  timezone: timeZone,
                },
              },
              optimisticResponse: {
                updateAccountTimezone: {
                  __typename: 'UpdateAccountTimezoneSuccess',
                  timezone: timeZone,
                },
              },
              update: (cache) => {
                // Update the account timezone in the cache
                cache.modify({
                  id: cache.identify({ __typename: 'Account' }),
                  fields: {
                    timezone: () => timeZone,
                  },
                })
              },
            })

          if (
            data?.updateAccountTimezone?.__typename === 'UnexpectedError' ||
            data?.updateAccountTimezone?.__typename === 'InvalidInputError' ||
            data?.updateAccountTimezone?.__typename === 'NotFoundError'
          ) {
            const timezoneResponseError = new Error(
              data.updateAccountTimezone.message,
            )
            logError(timezoneResponseError, {
              metaData: { data, error: mutationError },
            })
            throw timezoneResponseError
          }

          // Handle GraphQL errors if any
          if (
            mutationError ||
            data?.updateAccountTimezone?.__typename !==
              'UpdateAccountTimezoneSuccess'
          ) {
            const unexpectedError = new Error(
              mutationError?.message || 'Unexpected API response',
            )
            logError(unexpectedError, {
              metaData: { data, error: mutationError },
            })
            throw unexpectedError
          }
        } catch (caughtError) {
          logError(
            caughtError instanceof Error
              ? caughtError
              : new Error(String(caughtError)),
            {
              metaData: { variables: { timezone: timeZone } },
            },
          )
          throw caughtError
        }
      },
      [executeUpdateAccountTimezone],
    )

    return {
      updateTimeZone,
      isLoading: loading,
      hasError: !!error,
    }
  }
